import React, { forwardRef, useEffect, useState } from 'react'
import { BottomSheetModal, BottomSheetScrollView, BottomSheetView } from '@gorhom/bottom-sheet'
import { View, Platform, useWindowDimensions } from 'react-native'
import { DS } from '../../design-system'
import { Card } from '../../components/TempNewDesign/Card'
import ArrowTopRight from '../../components/ANewDesign/PlayerHand/assets/ArrowTopRight.svg'
import ArrowRight from '../../components/ANewDesign/PlayerHand/assets/ArrowRight.svg'
import X from '../../components/ANewDesign/PlayerHand/assets/X.svg'
import { fontFamily } from '../../theme'
import { SvgProps } from 'react-native-svg'
import { ScrollView } from 'react-native-gesture-handler'
import { ReduceMotion } from 'react-native-reanimated'

const cardStyles = {
  fontSize: 15,
  cardStyle: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 4,
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 4
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    borderWidth: 1,
    borderColor: DS.palette['Global/White']
  },
  cardTextStyle: { fontFamily: fontFamily.FeatureDeckRegular }
}

interface IHandProps {
  children: React.ReactNode
  name: string
}

const Hand: React.FC<IHandProps> = ({ name, children }) => {
  return (
    <View style={{ gap: DS.spacing[8] }}>
      <View style={{ flexDirection: 'row' }}>{children}</View>
      <DS.Text variant="Caption/Regular" style={{ alignSelf: 'center' }}>
        {name}
      </DS.Text>
    </View>
  )
}

const Divider = () => <View style={{ width: '100%', borderWidth: 1, borderColor: DS.palette['Brand/Light Grey'] }} />

const Action: React.FC<{
  name: string
  description: string
  Icon: React.FC<SvgProps>
  iconColor: string
  iconBg: string
}> = ({ description, Icon, iconBg, iconColor, name }) => {
  return (
    <View style={{ flexDirection: 'row', gap: DS.spacing[16] }}>
      <View
        style={{
          backgroundColor: iconBg,
          width: 28,
          height: 28,
          borderRadius: 50,
          borderWidth: 1,
          borderColor: DS.palette['Global/White'],
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Icon width={20} height={20} color={iconColor} />
      </View>
      <View style={{ gap: 2, flex: 1 }}>
        <DS.Text variant="Body Medium/Medium">{name}</DS.Text>
        <DS.Text variant="Body Small/Regular">{description}</DS.Text>
      </View>
    </View>
  )
}

interface IThreeCardHowToPlayProps {
  isPreGame?: boolean
  isReady?: boolean
  isPlayingGame?: boolean
  handleLetsPlay?: () => void
}
export const ThreeCardHowToPlay = forwardRef<BottomSheetModal, IThreeCardHowToPlayProps>(
  ({ isReady: isReadyProp, handleLetsPlay, isPlayingGame }, ref) => {
    const [isReady, setIsReady] = useState(false)
    const [isLetsPlayClicked, setLetsPlayClicked] = useState(false)
    const { height } = useWindowDimensions()
    useEffect(() => {
      if (isReadyProp) {
        setIsReady(true)
      }
      if (isPlayingGame && isReady) {
        setLetsPlayClicked(true)
      }
    }, [isReadyProp, isPlayingGame, isReady])

    return (
      <BottomSheetModal
        ref={ref}
        index={0}
        overrideReduceMotion={ReduceMotion.System}
        snapPoints={!isLetsPlayClicked ? [height / 2, height - 69] : [425, 640]}
        detached={false}
        // enablePanDownToClose={isLetsPlayClicked}
        enablePanDownToClose={true} // Avoid hard stuck
        keyboardBlurBehavior="restore"
        containerStyle={{
          backgroundColor: `${DS.palette['Global/Black']}50`
        }}
        backgroundStyle={{
          backgroundColor: DS.palette['Brand/Cream'],
          borderRadius: DS.spacing[16]
        }}
        handleIndicatorStyle={{
          width: 45,
          backgroundColor: `${DS.palette['Brand/Dark Grey']}40`,
          height: 4
        }}
      >
        <BottomSheetView>
          <BottomSheetScrollView
            contentContainerStyle={{
              paddingTop: !isLetsPlayClicked ? DS.spacing['32'] : DS.spacing[8],
              paddingBottom: DS.spacing[24],
              paddingHorizontal: DS.spacing[24],
              gap: DS.spacing[16]
            }}
            onScroll={() => {
              // @ts-ignore
              ref?.current?.expand()
            }}
          >
            <DS.Frame gap="16" hidden={isLetsPlayClicked}>
              <DS.Frame
                bg="Global/White"
                gap="24"
                style={{
                  paddingTop: 40,
                  paddingHorizontal: 24,
                  paddingBottom: 24,
                  borderRadius: 12,
                  ...Platform.select({
                    ios: {
                      shadowColor: '#000000',
                      shadowRadius: 2,
                      shadowOffset: { height: 0, width: 2 },
                      shadowOpacity: 0.05
                    },
                    android: {
                      elevation: 2
                    }
                  })
                }}
              >
                <DS.Frame
                  style={{
                    position: 'absolute',
                    top: -28,
                    right: 24,
                    width: '100%',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <DS.Icon name="Assets/Chips" size={56} />
                </DS.Frame>
                <DS.Frame gap="8" style={{ alignSelf: 'center', alignItems: 'center' }}>
                  <DS.Text variant="Body Medium/Regular">Welcome to</DS.Text>
                  <DS.Text variant="Heading 2/Medium">3-Card Pokerish</DS.Text>
                  <DS.Text variant="Body Medium/Regular" textAlign="center">
                    Challenge Jenny and Taylor to 5 rounds and compete for the best stats!
                  </DS.Text>
                </DS.Frame>
                <DS.Button
                  variant="Large/Primary"
                  type="On Light"
                  disabled={!isReady}
                  onPress={() => {
                    handleLetsPlay?.()
                    // @ts-ignore
                    ref?.current?.dismiss()
                    setLetsPlayClicked(true)
                  }}
                >
                  Let's Play
                </DS.Button>
              </DS.Frame>
              <DS.Frame gap="4" style={{ alignItems: 'center' }}>
                <DS.Text variant="Heading 4/Regular">Learn how to play</DS.Text>
                <DS.Icon name="Arrow---down" size={24} color="Global/Black" />
              </DS.Frame>
            </DS.Frame>
            <DS.Frame gap="4" style={{ alignSelf: 'center' }} hidden={!isLetsPlayClicked}>
              <DS.Text variant="Heading 2/Medium">3-Card Pokerish</DS.Text>
              <DS.Text variant="Body Medium/Regular" textAlign="center">
                New challenge daily!
              </DS.Text>
            </DS.Frame>
            <Divider />
            <View style={{ gap: DS.spacing[4], alignSelf: 'flex-start' }}>
              <DS.Text variant="Heading 5/Medium">How to win</DS.Text>
              <DS.Text variant="Body Small/Regular">
                The player with the best hand (or the last player left after everyone else folds) wins the pot!
              </DS.Text>
            </View>
            <View style={{ flexDirection: 'row', gap: DS.spacing[24] }}>
              <Hand name="High Card">
                <Card {...cardStyles} card="Kd" />
                <Card {...cardStyles} card="9s" disabled />
                <Card {...cardStyles} card="4d" disabled />
              </Hand>
              <Hand name="Pair">
                <Card {...cardStyles} card="7c" />
                <Card {...cardStyles} card="7d" />
                <Card {...cardStyles} card="As" disabled />
              </Hand>
              <Hand name="3 of a Kind">
                <Card {...cardStyles} card="Jh" />
                <Card {...cardStyles} card="Jd" />
                <Card {...cardStyles} card="Jc" />
              </Hand>
            </View>
            <Divider />
            <DS.Text variant="Heading 5/Medium">Actions</DS.Text>
            <Action
              Icon={ArrowTopRight}
              iconBg={DS.palette['Global/Black']}
              iconColor={DS.palette['Global/White']}
              name="Raise"
              description="Increase the bet amount. “I really like my cards, I want to add more chips.”"
            />
            <Action
              Icon={ArrowRight}
              iconBg={DS.palette['Global/White']}
              iconColor={DS.palette['Global/Black']}
              name="Match"
              description="Match the current bet. “I like my cards but just enough to match the current bet.”"
            />
            <Action
              Icon={ArrowRight}
              iconBg={DS.palette['Global/White']}
              iconColor={DS.palette['Global/Black']}
              name="Play"
              description="Pass the turn without betting. “I like my cards but not enough to bet more chips.”"
            />
            <Action
              Icon={X}
              iconBg={DS.palette['Brand/Maroon']}
              iconColor={DS.palette['Global/White']}
              name="Fold"
              description="Exit the hand. “I don't like my cards, I will sit this one out.”"
            />
          </BottomSheetScrollView>
        </BottomSheetView>
      </BottomSheetModal>
    )
  }
)
